import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { getValidTimezone } from "../../utils/dateFormatter";
import MethanolBarChart from "./MethanolBarChart";
const BAR_COLOR = "#1AC69E";
const ENERGY_UNIT = "kWh";
const DATE_PAD_LENGTH = 2;
const DATE_PAD_CHAR = "0";
const DATE_PART_LENGTH = 10;
const ISO_DATE_SEPARATOR = "T";
const NA_VALUE = "N/A";
const DEFAULT_TIMEZONE = "UTC";
const DEFAULT_X_AXIS_LABEL = "Days";
const NO_DATA_HEIGHT = "h-[245px]";
const Y_AXIS_TICK_COUNT = 5;
const MIN_MAX_ENERGY = 1;
const MIN_STEP_SIZE = 1;
// ── Helpers ───────────────────────────────────────────────────
const formatDateMMDD = (date) => {
    const month = String(date.getUTCMonth() + 1).padStart(DATE_PAD_LENGTH, DATE_PAD_CHAR);
    const day = String(date.getUTCDate()).padStart(DATE_PAD_LENGTH, DATE_PAD_CHAR);
    return `${month}/${day}`;
};
const formatTooltipDate = (date, timezone) => {
    const safeTimezone = getValidTimezone(timezone) ?? "UTC";
    const monthName = date.toLocaleString("en-US", { month: "long", timeZone: safeTimezone });
    const day = date.toLocaleString("en-US", { day: "numeric", timeZone: safeTimezone });
    return `${day} ${monthName}`;
};
// ── Fill missing dates with N/A ───────────────────────────────
export const fillMissingDates = (data, startDate, endDate) => {
    const result = [];
    const currentDate = new Date(startDate);
    const end = new Date(endDate);
    while (currentDate <= end) {
        const existing = data.find((item) => new Date(item.date).getTime() === currentDate.getTime());
        if (existing) {
            result.push(existing);
        }
        else {
            result.push({
                date: currentDate.toISOString().split(ISO_DATE_SEPARATOR)[0],
                energyDeliveredToSite: NA_VALUE
            });
        }
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }
    return result;
};
// ── Component ─────────────────────────────────────────────────
const MethanolEnergyBarChart = ({ fromDate, legendLabel, statDetails, timezone = DEFAULT_TIMEZONE, toDate, xAxisLabel = DEFAULT_X_AXIS_LABEL }) => {
    if (!statDetails.length) {
        return (_jsx("div", { className: `flex justify-center items-center ${NO_DATA_HEIGHT}`, "data-test": "methanol-energy-no-data", children: "No data available" }));
    }
    // Transform API data into the chart-friendly shape
    const sampleInfo = statDetails.map((d) => ({
        date: d.startTimestamp.substring(0, DATE_PART_LENGTH),
        energyDeliveredToSite: d.energyDetails?.chargeEnergy?.value ?? 0
    }));
    // Use the selected date range; fall back to min/max of data if not provided
    const startDateStr = fromDate
        ? fromDate.substring(0, DATE_PART_LENGTH)
        : new Date(sampleInfo.reduce((min, item) => Math.min(min, new Date(item.date).getTime()), new Date(sampleInfo[0].date).getTime()))
            .toISOString()
            .split(ISO_DATE_SEPARATOR)[0];
    const endDateStr = toDate
        ? toDate.substring(0, DATE_PART_LENGTH)
        : new Date(sampleInfo.reduce((max, item) => Math.max(max, new Date(item.date).getTime()), new Date(sampleInfo[0].date).getTime()))
            .toISOString()
            .split(ISO_DATE_SEPARATOR)[0];
    const filledData = fillMissingDates(sampleInfo, startDateStr, endDateStr);
    const maxEnergy = Math.max(MIN_MAX_ENERGY, ...sampleInfo.map((item) => Number(item.energyDeliveredToSite)));
    const yAxisStepSize = Math.ceil(maxEnergy / Y_AXIS_TICK_COUNT) || MIN_STEP_SIZE;
    const chartData = filledData.map((item) => {
        const date = new Date(item.date);
        return {
            date: formatDateMMDD(date),
            tooltipDate: formatTooltipDate(date, timezone),
            value: item.energyDeliveredToSite
        };
    });
    return (_jsx("div", { "data-test": "methanol-energy-bar-chart-container", children: _jsx(MethanolBarChart, { barColor: BAR_COLOR, data: chartData, isEnergyBarChart: true, legendLabel: legendLabel, testId: "methanol-energy-bar-chart", unit: ENERGY_UNIT, xAxisLabel: xAxisLabel, yAxisMax: maxEnergy, yAxisStepSize: yAxisStepSize }) }));
};
export default MethanolEnergyBarChart;
